import React from 'react';

import { Emphasis } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import {
    LanguageSelectButton,
    LanguageSelectButtonProps,
} from '@lumapps/translations/components/LanguageSelectButton/LanguageSelectButton';

import { useFormField } from '../../hooks/useFormField';
import { BaseInput } from '../../types';

export type FormLanguageSelectButtonProps = Pick<
    LanguageSelectButtonProps,
    'defaultLanguage' | 'languages' | 'className' | 'shouldAdaptToScreen' | 'isDisabled' | 'placement'
> &
    Omit<BaseInput, 'name'> & {
        name?: string;
    };

/**
 * Component to be used for selecting a language in the context of a multilanguage form
 * @family Forms
 * @param FormLanguageSelectButtonProps
 * @returns FormLanguageSelectButton
 */
export const FormLanguageSelectButton: React.FC<FormLanguageSelectButtonProps & Omit<BaseInput, 'name'>> = ({
    defaultLanguage,
    name = 'lang',
    controllerProps,
    languages,
    isRequired,
    isDisabled,
    className,
    shouldAdaptToScreen = true,
    placement,
}) => {
    const { translateKey } = useTranslate();
    const { field, isFormLoading, isFieldDisabled, valueToUse, scope } = useFormField({
        name,
        isRequired,
        controllerProps: {
            ...controllerProps,
            defaultValue: defaultLanguage,
        },
        label: translateKey(GLOBAL.LANGUAGE),
    });

    return (
        <LanguageSelectButton
            className={className}
            defaultLanguage={valueToUse}
            inputLanguage={defaultLanguage}
            scope={scope}
            isDisabled={isFieldDisabled || isDisabled || isFormLoading}
            onChange={field.onChange}
            languages={languages}
            isVisible
            shouldAdaptToScreen={shouldAdaptToScreen}
            emphasis={Emphasis.medium}
            placement={placement}
        />
    );
};
